import { SPACE_OVERVIEW, COMPANY_HUB } from '@confluence/named-routes/entry-points/routes.ts';
import { preloadSpaceOverviewId } from '@confluence/content-types-header/entry-points/preloadSpaceOverviewId.js';
import { preloadContentTypesHeader } from '@confluence/content-types-header/entry-points/preloadContentTypesHeader.js';
import { preloadConnectWebResources } from '@confluence/fabric-extension-handlers/entry-points/preloadConnectWebResources.ts';
import { preloadSpaceOverview } from '@confluence/space-overview/entry-points/preloadSpaceOverview.ts';
import { preloadMediaToken } from '@confluence/fabric-media-support/entry-points/preloadMediaToken.ts';
import { preloadPageTitleContentProperties } from '@confluence/content-topper/entry-points/preloadPageTitleContentProperties.ts';
import { preloadAutohighlights } from '@confluence/contextual-reading-aids/entry-points/preloadAutohighlights.ts';
import { preloadShortcutsSmartLinks } from '@confluence/space-shortcuts/entry-points/preloadShortcutsSmartLinks.ts';
import { preloadSideNavigation } from '@confluence/side-navigation/entry-points/preloadSideNavigation.ts';
import { preloadSpaceGuestList } from '@confluence/space-guest-list/entry-points/preloadSpaceGuestList.ts';
import { preloadSpaceDetail } from '@confluence/space-utils/entry-points/index.ts';
import { preloadAudienceSegmentationContext } from '@confluence/custom-sites-extensions/entry-points/preloadAudienceSegmentationContext.ts';
import { preloadPageRestrictionsQuery } from '@confluence/page-restrictions-context/entry-points/preloadPageRestrictionsQuery.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { prepareGuardExceptionTask } from '@confluence/query-preloader-tools/entry-points/prepareGuardExceptionTask.ts';
import { getCompanyHubSpaceKeyForSSR } from '@confluence/route-manager/entry-points/getCompanyHubSpaceKeyForSSR.ts';
import { preloadShareAndRestrictButtonQuery } from '@confluence/share-and-restrict-dialog/entry-points/preloadShareAndRestrictButtonQuery.ts';
import { preloadPresenceSettings } from '@confluence/team-presence/entry-points/preloadPresenceSettings.ts';
import { preloadContentMode } from '@confluence/editor-content-mode-button/entry-points/preloadContentMode.ts';
import {
	getSingleParam,
	getSingleParamInt,
} from '@confluence/route-manager-utils/entry-points/getSingleParam.ts';
import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { preloadSpaceViews } from '@confluence/space-views/entry-points/preloadSpaceViews.ts';
import { preloadNavigationTasks } from '@confluence/query-preloaders-common/entry-points/preloadNavigationTasks.ts';
import { fg } from '@confluence/feature-gating';
import {
	ATL_GENERAL,
	ATL_FOOTER,
} from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import {
	preloadWebPanelLocation,
	preloadWebPanelLocations,
} from '@confluence/web-panel-location/entry-points/preloadWebPanelLocation.ts';
import { preloadSuperBatchData } from '@confluence/wrm/entry-points/preloadSuperBatchData.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { getCachedPageData } from '@confluence/live-pages-utils/entry-points/getCachedPageData.ts';
import { preloadCrossJoinNthUserOnboardingState } from '@confluence/onboarding-helpers/entry-points/preloadCrossJoinNthUserOnboardingState.ts';

const homepageIdPromiseMap = new Map<string, Promise<string | undefined>>();
const homepageIdMap = new Map<string, string | undefined>();

const loadHomepageId = async (spaceKey: string): Promise<string | undefined> => {
	const { result, hasErrors } = await preloadSpaceOverviewId(spaceKey);
	return (!hasErrors && result?.data?.space?.homepageId) || null;
};

export const preloadSpaceOverviewRouteCritical = async (match: RouteMatch): Promise<void> => {
	const { spaceKey } = match.params;
	const homepageId = match.query
		? getSingleParam(match.query, SPACE_OVERVIEW.HOMEPAGE_ID_QUERY_KEY)
		: undefined;

	if (homepageId) return;

	const homepageIdPromise = homepageIdPromiseMap.get(spaceKey) ?? loadHomepageId(spaceKey);

	// Keep both maps in sync - promises and actual values for synchronous access
	homepageIdPromiseMap.set(spaceKey, homepageIdPromise);
	return homepageIdPromise.then((homepageId) => {
		homepageIdMap.set(spaceKey, homepageId);
	});
};

export const preloadSpaceOverviewRoute = async (
	match: RouteMatch,
	previousMatch: RouteMatch | null,
	_url: string,
): Promise<void | any[] | Error[]> => {
	const { cloudId, isLicensed, userId, activationId, isLoggedIn } = getPreloaderFnContext();
	const { spaceKey } = match.params;
	const initialHomepageId = match.query
		? getSingleParam(match.query, SPACE_OVERVIEW.HOMEPAGE_ID_QUERY_KEY)
		: undefined;

	const tasks: Promise<any>[] = [];

	if (spaceKey && !fg('space_overview_preloader_consolidation')) {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			// Only load data for navigation in React SSR since we are delaying them in the SPA
			tasks.push(
				preloadShortcutsSmartLinks(spaceKey),
				preloadSideNavigation(spaceKey),
				preloadSpaceGuestList(spaceKey),
				preloadSpaceViews(spaceKey, isLicensed),
			);
		}
	}

	const isCompanyHub = match.name === COMPANY_HUB.name;
	const companyHubSpaceKey = isCompanyHub ? await getCompanyHubSpaceKeyForSSR() : undefined;
	const versionOverride =
		companyHubSpaceKey && match.query ? getSingleParamInt(match.query, 'versionOverride') : null;

	const homepageId = initialHomepageId || homepageIdMap.get(spaceKey ?? companyHubSpaceKey);

	if (spaceKey && fg('space_overview_preloader_consolidation')) {
		// Consolidated navigation preloading: use the shared helper (page tree + blog tree included)
		// like the view page route, instead of splitting nav queries between this route and
		// preloadSpaceOverview. Company hub has no `spaceKey` route param, so it is naturally excluded
		// here — preserving the prior behaviour where the page tree was not preloaded for company hub.
		const isCrossSpaceTransition = Boolean(
			previousMatch && previousMatch.params?.spaceKey !== spaceKey,
		);
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR || isCrossSpaceTransition) {
			tasks.push(
				preloadNavigationTasks(
					spaceKey,
					homepageId ?? '',
					isLicensed,
					false, // space overview is never a blog
					previousMatch?.params?.spaceKey,
				),
			);
		}
	}

	tasks.push(
		preloadSpaceOverview({
			spaceKey: spaceKey ?? companyHubSpaceKey,
			homepageId,
			homepageIdPromise: homepageIdPromiseMap.get(spaceKey ?? companyHubSpaceKey),
			previousMatch,
			versionOverride,
		}),
	);

	if (homepageId) {
		// Get isArchived from cached page data
		const { isArchived } = getCachedPageData(homepageId ?? undefined);
		tasks.push(
			preloadContentMode({ contentId: homepageId, isDraft: true, useCache: true, isArchived }),
		);
	}

	tasks.push(
		preloadPageTitleContentProperties({
			contentId: homepageId || '',
			contentIdPromise: homepageIdPromiseMap.get(spaceKey ?? companyHubSpaceKey),
			spaceKey: spaceKey ?? companyHubSpaceKey,
		}),
	);

	if (
		fg('content_early_connect_iframe_initialization') ||
		expVal('confluence_frontend_early_webpanel_initialization', 'isEnabled', false)
	) {
		// Preload superbatch
		tasks.push(
			prepareGuardExceptionTask('Superbatch', () =>
				preloadSuperBatchData({ preloadSuperBatchForSSR: true }),
			),
		);

		// Preload Connect web resources
		tasks.push(
			prepareGuardExceptionTask('Connect Web Resources', () =>
				preloadConnectWebResources(homepageId),
			),
		);
	}

	tasks.push(preloadSpaceDetail(spaceKey));

	if (expValEquals('cc_ssr_crossjoin_nth_user_onboarding', 'isEnabled', true)) {
		tasks.push(preloadCrossJoinNthUserOnboardingState());
	}

	if (isCompanyHub && userId) {
		tasks.push(
			preloadAudienceSegmentationContext({
				accountId: userId,
			}),
		);
	}

	// Presence Avatar Visibility
	tasks.push(preloadPresenceSettings({ cloudId, spaceKey }));

	// Space Overview's homepageId is needed for the content types header, but may not be in the URL
	// depending on which route was used.  skip preloading if it's not in the url because sequential calls block SSR
	if (!isCompanyHub && homepageId) {
		tasks.push(preloadContentTypesHeader(homepageId));
		// Standalone Restrictions button needs to have its data preloaded)
		tasks.push(preloadPageRestrictionsQuery(homepageId, false));
	}

	// Unified Share and Restrict Button
	if (!isCompanyHub && homepageId && fg('confluence_frontend_usd_ssr')) {
		tasks.push(preloadShareAndRestrictButtonQuery(homepageId));
	}

	// Preload definition autohighlights
	// Only preload autohighlights for authenticated users (knowledge_discovery service requires auth)
	if (homepageId && isLoggedIn) {
		tasks.push(
			preloadAutohighlights({
				contentId: homepageId,
				cloudId,
				activationId,
				contentType: 'page',
			}),
		);
	}

	// Preload media token for cover image
	if (homepageId) {
		tasks.push(preloadMediaToken(homepageId, cloudId));
	}

	// Skip if early webpanel initialization is enabled, as we already query it in preloadConnectWebResources
	if (
		homepageId &&
		!expVal('confluence_frontend_early_webpanel_initialization', 'isEnabled', false)
	) {
		if (expValEquals('web_panel_query_batch_call', 'isEnabled', true)) {
			tasks.push(
				preloadWebPanelLocations({
					contentId: homepageId,
					locations: [ATL_GENERAL, ATL_FOOTER],
				}),
			);
		} else {
			tasks.push(
				preloadWebPanelLocation({ contentId: homepageId, location: ATL_GENERAL }),
				preloadWebPanelLocation({ contentId: homepageId, location: ATL_FOOTER }),
			);
		}
	}

	if (!isCompanyHub && !homepageId) {
		// Unified Share and Restrict Button - preload using spaceKey to avoid chained queries
		if (fg('confluence_frontend_usd_ssr')) {
			tasks.push(preloadShareAndRestrictButtonQuery(undefined, spaceKey));
		}

		//moved chained queries to the bottom to unblock other queries needed to preload & to combine the contentId query
		const homepageIdPromise = homepageIdPromiseMap.get(spaceKey) || loadHomepageId(spaceKey);
		await homepageIdPromise.then((contentId) => {
			tasks.push(preloadContentTypesHeader(contentId));
			tasks.push(preloadPageRestrictionsQuery(contentId ?? '', false));

			if (contentId) {
				// Preload web panel atl_general
				if (expValEquals('web_panel_query_batch_call', 'isEnabled', true)) {
					tasks.push(
						preloadWebPanelLocations({
							contentId,
							locations: [ATL_GENERAL, ATL_FOOTER],
						}),
					);
				} else {
					tasks.push(
						preloadWebPanelLocation({ contentId, location: ATL_GENERAL }),
						preloadWebPanelLocation({ contentId, location: ATL_FOOTER }),
					);
				}

				// Preload media token for cover image
				tasks.push(preloadMediaToken(contentId, cloudId));
			}
		});
	}

	return waitForAllReturnWhenError(tasks);
};

// WARNING: Exposed for testing only
export const _clearHomepageIdMaps = (): void => {
	homepageIdMap.clear();
	homepageIdPromiseMap.clear();
};
